import { Asset } from '../../../models/image/dto/asset';
import { MediaManagerAssetLibraryDto } from '../../../models/image/dto/media-manager-asset-library-dto';
import { UploadFilePath } from '../../../models/enum/dto/upload-file.path';
import { applyCreatedAsset, applyDeletedAssets, applyUpdatedAsset } from './asset-patch';
import { MediaManagerFileSystem } from 'src/app/models/media-manager/media-manager-file-system';

/**
 * Replace an existing library asset by matching on `id` alone rather than the composite unique id.
 *
 * Transcoding can change an asset's md5Hash (the input hash differs from the transcoded output hash), so a
 * finalized transcode payload will not compare equal to the placeholder that was uploaded. When the unique id
 * already matches, we do an in-place update; otherwise the stale placeholder is removed and the new asset is
 * created fresh.
 */
export function replaceAssetByIdInMemoryState(
  completedAsset: Asset,
  filesystem: MediaManagerFileSystem,
  assetLibrary: MediaManagerAssetLibraryDto
): boolean {
  const existingAsset = (assetLibrary.assets ?? []).find(asset => asset.id === completedAsset.id) ?? null;
  if (existingAsset && existingAsset.getUniqueIdentifier() === completedAsset.getUniqueIdentifier()) {
    return applyUpdatedAsset(existingAsset, completedAsset, filesystem);
  }
  if (existingAsset && !applyDeletedAssets([existingAsset.getUniqueIdentifier()], filesystem, assetLibrary)) {
    return false;
  }
  return applyCreatedAsset(completedAsset, filesystem, assetLibrary);
}

/** Rehydrate an uploaded asset with a stable parentFolderId, defaulting to the target folder or 'ROOT'. */
export function normalizeUploadedAssetForTargetFolder(uploadedAsset: Asset, targetFolderId: string): Asset {
  return window?.injector?.Deserialize?.instanceOf(Asset, {
    ...uploadedAsset,
    parentFolderId: uploadedAsset?.parentFolderId?.trim() || targetFolderId?.trim() || 'ROOT'
  });
}

/**
 * Rehydrate an asset copied into Media Manager with the AssetLibrary media class and a stable parent folder.
 * Falls back to the raw shape when the injector is unavailable (e.g. tests) rather than returning null.
 */
export function normalizeCopiedAssetForMediaManager(copiedAsset: Asset, targetFolderId: string): Asset {
  const normalizedCopiedAsset = {
    ...copiedAsset,
    mediaClass: UploadFilePath.AssetLibraryPath,
    parentFolderId: copiedAsset?.parentFolderId?.trim() || targetFolderId?.trim() || 'ROOT'
  };
  return window?.injector?.Deserialize?.instanceOf(Asset, normalizedCopiedAsset) ?? (normalizedCopiedAsset as Asset);
}

/** Normalize a raw folder id (null/undefined/whitespace) to the 'ROOT' sentinel used by the MMFS. */
export function normalizeFolderIdForAssetLibrary(folderId: string | null | undefined): string {
  return folderId?.trim() || 'ROOT';
}

/** Convert an MMFS folder id to the empty-string sentinel the asset-library backend uses for root. */
export function toBackendAssetLibraryParentFolderId(folderId: string | null | undefined): string {
  const normalizedFolderId = normalizeFolderIdForAssetLibrary(folderId);
  return normalizedFolderId === 'ROOT' ? '' : normalizedFolderId;
}
